import React, { useContext, useState, useEffect } from 'react';
import { WindowSizeContext } from '/src/App';
import './style.css';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import TwoLineChart from '/src/Components/TwoLineChart/TwoLineChart';
import OneLineChart from '/src/Components/OneLineChart/OneLineChart';
import AnalysisSectionHeader from '/src/Components/AnalysisSectionHeader/AnalysisSectionHeader';
import BudgetingTable from './BudgetingTable/BudgetingTable';
import RiskOverviewStandardSectionHeader from '/src/Components/RiskOverviewStandardSectionHeader/RiskOverviewStandardSectionHeader';
// import RiskOverviewSectionHeader from '../RiskOverviewSectionHeader/RiskOverviewSectionHeader';
import { Doughnut } from 'react-chartjs-2';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import RiskOverviewClimateIcon from '/src/Components/RiskOverviewClimateIcon/RiskOverviewClimateIcon';
import RiskOverviewSectionLoadingState from '/src/Components/RiskOverviewSectionLoadingState/RiskOverviewSectionLoadingState';
import HowWorriedShouldIBe from '/src/Components/HowWorriedShouldIBe/HowWorriedShouldIBe';
import { AreaDataContext } from '../../PropertyExplorer';
import RisksToHomeRelationships from '/src/Components/RisksToHomeRelationships/RisksToHomeRelationships';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js';

// Register the chart components
ChartJS.register(
    ArcElement,
    Tooltip,
    Legend
);

const Budgeting = ({ propertyData, title }) => {
    const { deviceType } = useContext(WindowSizeContext);
    const { contentLoaded, userPersonalInfo, quollScoreAnalysis } = useContext(AreaDataContext);
    const new_budgeting_intro_text = "Sudden climate events can mean tremendous out of pocket costs. Yes, you may get some of the money back from insurance (which you can't always count on), but even if you do, you may be waiting a while - sometimes it can be years. So having enough funds available to cover unforeseen events is critical. "

    // if (!contentLoaded) {
    //     return <RiskOverviewSectionLoadingState title={title} intro_sentence={new_budgeting_intro_text} />;
    // }

    const property_areas_threatened = propertyData?.data?.property_areas_threatened?.house_categories_with_risks || [];

    const climate_risks = propertyData?.data?.climate_risks || [];

    // Map numeric IDs to house area classes based on the actual data
    const getAreaClass = (id) => {
        const areaMap = {
            '2': 'roof',           // First object in array has id '2'
            '1': 'landscaping',    // Second object has id '1'
            '6': 'windows',        // Third object has id '6'
            '10': 'hvac',          // Fourth object has id '10'
            '7': 'doors',          // Fifth object has id '7'
            '14': 'frame',         // Sixth object has id '14'
            '4': 'foundation',     // Seventh object has id '4'
            '9': 'siding',         // Eighth object has id '9'
            '5': 'plumbing',       // Ninth object has id '5'
            '13': 'basement'       // Tenth object has id '13'
        };
        return areaMap[id] || 'unknown';
    };

    const getRiskColor = (risk_level) => {
        switch (risk_level.toLowerCase()) {
            case 'high': return 'var(--related_red)';
            case 'medium': return 'var(--yellow)';
            case 'low': return 'var(--light-grey)';
            default: return 'var(--light-grey)';
        }
    };

    const new_hub_house_image_filename = "house-lansdcape.webp";
    const new_hub_house_image_url = CreateCloudfrontUrl(new_hub_house_image_filename);


    // Set the status for the should I be worried sentence and meter level
    const [shouldIBeWorriedSentence, setShouldIBeWorriedSentence] = useState(null);
    const [meterLevel, setMeterLevel] = useState(0);

    // Set the states for when a user hovers over a climate risk or a house area
    const [hoveredClimateRisk, setHoveredClimateRisk] = useState(null);
    const [hoveredHouseArea, setHoveredHouseArea] = useState(null);

    // Set up the handlers for when a user hovers over a climate risk or a house area and then why they leave the hover
    const handleClimateRiskHover = (risk) => {
        // console.log(risk);
        setHoveredClimateRisk(risk);
    };

    const handleClimateRiskLeave = () => {
        setHoveredClimateRisk(null);
    };

    const handleHouseAreaHover = (area) => {
        // console.log(area);
        setHoveredHouseArea(area);
    };

    const handleHouseAreaLeave = () => {
        setHoveredHouseArea(null);
    };

    // State for the table
    const [showTable, setShowTable] = useState(false);
    const [houseAreasThreatened, setHouseAreasThreatened] = useState([]);

    const houseAreaColors = [
        '#115453',  // Darkest teal
        '#166E6D',
        '#1B8180',
        '#209493',
        '#25ADAC',
        '#2AC6C5',
        '#2FD9D7',
        '#34ECEB',
        '#39FFFF',  // Lightest teal
        '#45FFFF'   // Extra light teal
    ]

    // Process property_areas_threatened data
    useEffect(() => {
        const house_areas_threatened = propertyData?.data?.property_areas_threatened?.house_categories_with_risks;

        if (!house_areas_threatened) return;

        // Ensure we're working with an array
        const areas = Array.isArray(house_areas_threatened) ?
            house_areas_threatened : [];


        // 1. Calculate total risk across all areas
        const totalRisk = areas.reduce((sum, area) => {
            return sum + (area.rebuild_risk_weighted_likelihood_est || 0);
        }, 0);


        // 2 & 3. Calculate percentage for each area and add to object
        const updatedAreas = areas.map((area, index) => ({
            ...area,
            color: houseAreaColors[index],
            risk_pct_doughnut: totalRisk ?
                ((area.rebuild_risk_weighted_likelihood_est || 0) / totalRisk) * 100 :
                0
        }));

        // Sort the areas by risk_pct_doughnut in descending order
        updatedAreas.sort((a, b) => b.risk_pct_doughnut - a.risk_pct_doughnut);

        // After sorting, add a new key to each called risk_level_adjusted and make it an if statement, checking the risk_pct_doughnut
        updatedAreas.forEach(area => {
            area.risk_level_adjusted = area.risk_pct_doughnut > 10 ? 'high' : area.risk_pct_doughnut > 5 ? 'medium' : 'low';
        });


        setHouseAreasThreatened(updatedAreas);
    }, [propertyData]);

    // console.log(houseAreasThreatened)


    // Header and intro text
    const budgeting_header = "What kind of sudden costs might that exposure create?"
    const budgeting_intro_text = "Setting aside budgeted savings for unforeseen costs is crucial for recovering from unexpected weather events. Insurance often falls short, with payouts taking years and requiring owners to cover upfront repair costs. Without opting for additional coverage, such as wind, fire, or flood, you may face sudden out-of-pocket expenses without emergency funds.<br/><br/>We recommend you have the following funds available for emergency repairs in each year:"

    const budgeting_conclusion_text = `Keep in mind, those numbers are <b><i>not</i></b> cumulative. What they represent is what the worst case out of pocket costs could be in a given year, <i>assuming you did nothing to make the home more resilient</i>.`
    // const property_areas_threatened = propertyData?.data?.property_areas_threatened?.house_categories_with_risks || [];




    // Naming for the two different sections and the chart title
    const noImprovementsTitle = "No Resilience Improvements"
    const withImprovementsTitle = "With Resilience Improvements"
    const chartTitle = "'Worst Case' Out-of-Pocket Costs Based on Current Home Resilience Level"

    // Transform the data to extract just the values
    const noImprovementsData = propertyData?.data?.scenario_modeling?.individual_breakdowns?.[0]
        ?.financials_no_improvements_made?.breakdown?.budgeting?.map(item => item.value) || [];

    const withImprovementsData = propertyData?.data?.scenario_modeling?.individual_breakdowns?.[0]
        ?.financials_with_improvements?.breakdown?.budgeting?.map(item => item.value) || [];

    const labels = Array.from({ length: 10 }, (_, i) => `Year ${i + 1}`);

    console.log(houseAreasThreatened);
    // Prepare doughnut chart data
    const doughnutData = {
        labels: houseAreasThreatened.map(area => area.category),
        datasets: [{
            data: houseAreasThreatened.map(area => area.risk_pct_doughnut),
            backgroundColor: [
                '#115453',  // Darkest teal
                '#166E6D',
                '#1B8180',
                '#209493',
                '#25ADAC',
                '#2AC6C5',
                '#2FD9D7',
                '#34ECEB',
                '#39FFFF',  // Lightest teal
                '#45FFFF'   // Extra light teal
            ],
            borderWidth: 0,
            hoverOffset: 4
        }]
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1,
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    font: {
                        family: 'inherit',
                        size: 14
                    },
                    padding: 10,
                    boxWidth: 12,
                    usePointStyle: true
                },
                maxWidth: '100%',
                align: 'center'
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const area = houseAreasThreatened[context.dataIndex];

                        const background_color = context.element.options.backgroundColor;
                        // console.log(background_color)
                        return `  ${area.risk_pct_doughnut.toFixed(1)}%`;
                    }
                }
            },
            datalabels: {
                display: false  // This will hide all data labels on the chart
            }
        },
        cutout: '60%'
    };

    // useEffect that listens to userPersonalInfo quollScoreAnalysis and sets the shouldIBeWorriedSentence
    useEffect(() => {

        // console.log('USER PERSONAL INFO', userPersonalInfo);
        // console.log('QUOLL SCORE ANALYSIS', quollScoreAnalysis);

        if (userPersonalInfo && quollScoreAnalysis) {

            // Get the available funds from the userPersonalInfo
            const availableFunds = userPersonalInfo.availableFunds || null;
            const availableFundsFormatted = availableFunds ? availableFunds.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : null;

            // Get the meter level from the quollScoreAnalysis
            const meterLevel = quollScoreAnalysis.budget.budgetRiskLevel;

            // console.log('WORRIED INPUTS', meterLevel, availableFundsFormatted);

            let sentence = null;
            // Get the sentence from the quollScoreAnalysis
            if (meterLevel && availableFundsFormatted) {
                if (meterLevel <= 3) {
                    sentence = `You do not need to worry about sudden costs skyrocketing past what you are able to confidently cover with your available funds of ${availableFundsFormatted}. This isn't to say you would be pleased, just that it doesn't pose an existential threat`;
                } else if (meterLevel <= 6) {
                    sentence = `You have enough available funds to cover the worst case out-of-pocket costs over the next 10 years, but it is probably not as much buffer as you might like, especially if that cash ends up needed elsewhere. Acting now to stay clear of the outer edge is a good idea.`;
                } else {
                    sentence = `Sudden costs could be a significant challenge for you. You are already at risk of potentially not being able to cover the worst case out-of-pocket cost in a given year, especially if that cash ends up needed elsewhere. Acting now to stay clear of the outer edge is a good idea.`;
                }
            }

            setShouldIBeWorriedSentence(sentence);
            setMeterLevel(meterLevel);
        }
    }, [userPersonalInfo, quollScoreAnalysis]);

    return (
        <div className="tab-content-container">
            <RiskOverviewStandardSectionHeader title={title} />
            <p className="text-align-center" dangerouslySetInnerHTML={{ __html: new_budgeting_intro_text }} />
            <RisksToHomeRelationships
                propertyData={propertyData}
                climateRisks={climate_risks}
                houseAreasThreatened={houseAreasThreatened}
            />
            {/* <div className="house-risk-risks-and-areas-frame">
                <div className="house-risk-visualization-container">
                    <div className="house-risks-section">
                        <h3 className="section-subtitle">Key Risks for This Home</h3>
                        <div className="house-climate-risks">
                            <div className="house-climate-risks-row-1">
                                {climate_risks.slice(0, 2).map((risk) => {

                                    let notSelected = null;
                                    if ((hoveredClimateRisk && hoveredClimateRisk.risk_id !== risk.risk_id) || (hoveredHouseArea && !hoveredHouseArea.applicable_risks.includes(risk.risk_id))) {
                                        notSelected = "not-selected";
                                    }

                                    return (
                                        <RiskOverviewClimateIcon
                                            handleMouseEnter={() => handleClimateRiskHover(risk)}
                                            handleMouseLeave={handleClimateRiskLeave}
                                            notSelected={notSelected}
                                            key={risk.risk_id}
                                            id={risk.risk_id}
                                            risk_level={risk.risk_level}
                                            icon_white={risk.icon_white}
                                            icon_black={risk.icon_black}
                                            risk_name={risk.risk_name}
                                            requiresLookup={false}
                                        />
                                    )
                                })}
                            </div>
                            <div className="house-climate-risks-row-2">
                                {climate_risks.slice(2, 5).map((risk) => {
                                    let notSelected = null;
                                    if ((hoveredClimateRisk && hoveredClimateRisk.risk_id !== risk.risk_id) || (hoveredHouseArea && !hoveredHouseArea.applicable_risks.includes(risk.risk_id))) {
                                        notSelected = "not-selected";
                                    }

                                    return (
                                        <RiskOverviewClimateIcon
                                            handleMouseEnter={() => handleClimateRiskHover(risk)}
                                            handleMouseLeave={handleClimateRiskLeave}
                                            notSelected={notSelected}
                                            key={risk.risk_id}
                                            id={risk.risk_id}
                                            risk_level={risk.risk_level}
                                            icon_white={risk.icon_white}
                                            icon_black={risk.icon_black}
                                            risk_name={risk.risk_name}
                                            requiresLookup={false}
                                        />
                                    )
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="vertical-divider"></div>

                    <div className="house-diagram-section">
                        <h3 className="section-subtitle">Parts of Home Threatened</h3>
                        <div className="climate-factors-home-hub-container">
                            <img
                                className="climate-factors-home-hub-image"
                                src={new_hub_house_image_url}
                                alt="House diagram"
                            />
                            <div className="house-risk-markers">
                                {houseAreasThreatened.map((area) => {

                                    let notSelected;
                                    if ((hoveredHouseArea && hoveredHouseArea.id !== area.id) || (hoveredClimateRisk && !area.applicable_risks.includes(hoveredClimateRisk.risk_id))) {
                                        notSelected = "not-selected";
                                    }

                                    return (
                                        <div
                                            key={area.id}
                                            onMouseEnter={() => handleHouseAreaHover(area)}
                                            onMouseLeave={handleHouseAreaLeave}
                                            className={`house-risk-marker house-risk-marker-${getAreaClass(area.id)} ${area.risk_level_adjusted} ${notSelected}`}
                                  
                                        >
                                            {area.category}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                <p className="text-align-center hover-over-area-text">Hover over a climate risk or a house area to see the impact on the home.</p>
            </div> */}
            {/* <CurlyBraceDivider direction="down" color="grey" /> */}
            <div className={`budgeting-charts-container ${deviceType}`}>
                {/* Left side - Doughnut chart */}
                <div className={`risk-distribution-container ${deviceType}`}>
                    <h3 className="section-subtitle">Parts of Home Driving Potential Costs</h3>
                    <div className={`doughnut-chart-container ${deviceType}`}>
                        <Doughnut data={doughnutData} options={doughnutOptions} />
                    </div>
                    {/* <div className="doughnut-chart-legend-container">
                        {houseAreasThreatened.map((area) => {
                            return (
                                <div className="doughnut-chart-legend-item">
                                    <div className="doughnut-chart-legend-item-color" style={{ backgroundColor: area.color }}></div>
                                    <div className="doughnut-chart-legend-item-text-container">
                                        <div className="doughnut-chart-legend-item-text">{area.category}</div>
                                        <div className="doughnut-chart-legend-item-text-percentage">{area.risk_pct_doughnut.toFixed(1)}%</div>
                                    </div>
                                </div>
                            )
                        })}
                    </div> */}
                </div>
                {/* <CurlyBraceDivider direction="right" color="grey" /> */}
                {/* Right side - Line chart */}
                <div className={`cost-projection-container ${deviceType}`}>
                    <h3 className="section-subtitle">'Worst Case' Out-of-Pocket Costs Next 10 Years</h3>
                    <div className={`budgeting-line-chart ${deviceType}`}>
                        <OneLineChart
                            title={noImprovementsTitle}
                            data={noImprovementsData}
                            labels={labels}
                            deviceType={deviceType}
                            chartType="budgeting"
                        />
                    </div>
                </div>

            </div>


            <p className="text-align-center" dangerouslySetInnerHTML={{ __html: budgeting_conclusion_text }} />
            <HowWorriedShouldIBe
                meterLevel={meterLevel}
                sentence={shouldIBeWorriedSentence}
                userPersonalInfo={userPersonalInfo}
            />
        </div>
    );
};

export default Budgeting;

